import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnInit,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { UserRole } from '../user/user';
import { Userright, UserroleRight } from './userrole';
import { UserroleService } from './userrole.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';

type RightsSearchLevel = 'menu' | 'group' | 'right' | 'none';

interface RightsSearchMatch {
    menu: string;
    group: string;
    right: Userright;
}

interface GroupSearchMatch {
    menu: string;
    group: string;
}

interface UnifiedSearchMatch {
    level: Exclude<RightsSearchLevel, 'none'>;
    menu: string;
    group?: string;
    right?: Userright;
}

interface ParsedBezeichnung {
    menu: string;
    group: string;
    action: string;
}

@Component({
    selector: 'kiene-userrole',
    templateUrl: './userrole.component.html',
    styleUrls: ['./userrole.component.scss'],
})
export class UserroleComponent implements OnInit {
    @ViewChild(MatAutocompleteTrigger) userroleAutocompleteTrigger: MatAutocompleteTrigger;
    @ViewChild('userroleInput') userroleInput: ElementRef<HTMLInputElement>;
    @ViewChild('rightsSearchInput') rightsSearchInput: ElementRef<HTMLInputElement>;

    isLoading: boolean = false;
    userroles: UserRole[] = [];
    filteredUserroles: Observable<UserRole[]>;
    filteredUserrolesList: UserRole[] = [];
    tabCycleIndex = -1;
    userroleCtrl = new UntypedFormControl('');
    rightsSearchCtrl = new UntypedFormControl('');

    rightsSearchLevel: RightsSearchLevel = 'none';
    rightsSearchMatchedMenus: string[] = [];
    rightsSearchMatchedGroups: GroupSearchMatch[] = [];
    rightsSearchMatches: RightsSearchMatch[] = [];
    rightsSearchCycle: UnifiedSearchMatch[] = [];
    rightsSearchCycleIndex = 0;
    highlightedRightIds = new Set<number>();
    menusWithSearchMatches = new Set<string>();
    visibleGroupsByMenu = new Map<string, Set<string>>();

    private userExpandedMenus = new Set<string>();
    currentUserrole: UserRole;
    userroleRights: UserroleRight[] = [];
    userrights: Userright[] = [];

    menus: string[] = [];
    groups = [];
    actions = [];

    userrightArray: boolean[] = [];

    displayedColumns = ['benutzerrecht', 'deletebutton'];

    userrightMap = new Map<string, any>();

    constructor(
        private readonly service: UserroleService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly tableService: KieneTableService,
        private readonly messageService: MessageService,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.filteredUserroles = this.userroleCtrl.valueChanges.pipe(
            startWith(''),
            map(value => {
                if (typeof value === 'string') {
                    this.tabCycleIndex = -1;
                }
                const filtered = this.filterUserroles(value);
                if (this.tabCycleIndex >= filtered.length) {
                    this.tabCycleIndex = -1;
                }
                this.filteredUserrolesList = filtered;
                return filtered;
            }),
        );
        this.loadUserroles();
        this.loadAllUserrights();
        this.rightsSearchCtrl.valueChanges
            .pipe(
                debounceTime(250),
                distinctUntilChanged(
                    (previous, current) =>
                        this.normalizeSearchText(previous) === this.normalizeSearchText(current),
                ),
            )
            .subscribe(() => {
                try {
                    this.applyRightsSearch();
                } catch (error) {
                    console.error('Benutzerrecht search failed', error);
                }
            });
    }

    loadAllUserrights() {
        this.tableService.get(this.apiBaseUrl + 'benutzerrecht/read.php').subscribe({
            next: response => {
                this.userrights = response.records;
                for (let ur of this.userrights) {
                    this.userrightArray[ur.benutzerrecht_id] = false;
                }
                this.initUserrightMap();
                if (this.getRightsSearchTerm()) {
                    this.applyRightsSearch();
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    initUserrightMap() {
        this.userrightMap.clear();
        this.groups = [];
        this.actions = [];

        for (let ur of this.userrights) {
            const { menu, group } = this.parseBezeichnung(ur.bezeichnung);

            if (this.userrightMap.has(menu)) {
                const groupMap: Map<string, any> = this.userrightMap.get(menu);
                if (groupMap.has(group)) {
                    let actions: Userright[] = groupMap.get(group);
                    actions.push(ur);
                } else {
                    let actions: Userright[] = [];
                    actions.push(ur);
                    groupMap.set(group, actions);
                }
            } else {
                let actions: Userright[] = [];
                actions.push(ur);
                let groupMap = new Map<string, Userright[]>();
                groupMap.set(group, actions);
                this.userrightMap.set(menu, groupMap);
            }
        }

        this.menus = Array.from(this.userrightMap.keys());
        for (let m of this.menus) {
            this.groups[m] = Array.from(this.userrightMap.get(m).keys());
            for (let groupName of this.groups[m]) {
                this.actions[m + '-' + groupName] = this.userrightMap.get(m).get(groupName);
            }
        }
    }

    loadUserroles() {
        this.service.getAllUserroles().subscribe({
            next: response => {
                this.userroles = response.records;
                this.userroleCtrl.setValue(this.userroleCtrl.value ?? '', { emitEvent: true });
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    displayUserrole = (role: UserRole): string => role?.bezeichnung ?? '';

    onUserroleSelected(event: MatAutocompleteSelectedEvent): void {
        this.tabCycleIndex = -1;
        this.setSelectedRole(event.option.value);
    }

    onUserroleTab(event: Event): void {
        const filtered = this.filteredUserrolesList;
        if (!filtered.length) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.tabCycleIndex = (this.tabCycleIndex + 1) % filtered.length;
        this.userroleCtrl.setValue(filtered[this.tabCycleIndex], { emitEvent: false });
        this.userroleAutocompleteTrigger?.openPanel();
    }

    onUserroleEnter(event: Event): void {
        const filtered = this.filteredUserrolesList;
        if (!filtered.length) {
            return;
        }

        let role: UserRole | undefined;
        if (this.tabCycleIndex >= 0 && this.tabCycleIndex < filtered.length) {
            role = filtered[this.tabCycleIndex];
        } else if (filtered.length === 1) {
            role = filtered[0];
        }
        if (!role) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.tabCycleIndex = -1;
        this.userroleCtrl.setValue(role);
        this.setSelectedRole(role);
        this.userroleAutocompleteTrigger?.closePanel();
    }

    private filterUserroles(value: string | UserRole): UserRole[] {
        const filterValue = this.normalizeSearchText(
            typeof value === 'string' ? value : value?.bezeichnung || '',
        );
        if (!filterValue) {
            return this.userroles;
        }
        return this.userroles.filter(role => this.textIncludes(role.bezeichnung ?? '', filterValue));
    }

    setSelectedRole(userrole: UserRole) {
        this.currentUserrole = userrole;
        this.rightsSearchCtrl.setValue('');
        for (let ur of this.userrights) {
            this.userrightArray[ur.benutzerrecht_id] = false;
        }
        this.loadRightsForCurrentRole();
    }

    loadRightsForCurrentRole() {
        this.isLoading = true;
        this.service.getUserroleRights(this.currentUserrole).subscribe({
            next: response => {
                this.userroleRights = response.records;
                for (let ur of this.userroleRights) {
                    this.userrightArray[ur.benutzerrecht_id] = true;
                }
                this.isLoading = false;
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.isLoading = false;
            },
        });
    }

    setUserright(event: MatCheckboxChange, right: Userright) {
        if (event.checked) {
            this.service.addRightToRole(this.currentUserrole, right).subscribe({
                next: () => {
                    this.loadRightsForCurrentRole();
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        } else {
            this.service.removeRightFromRole(this.currentUserrole, right).subscribe({
                next: () => {
                    this.loadRightsForCurrentRole();
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        }
    }

    clearRightsSearch(): void {
        this.rightsSearchCtrl.setValue('', { emitEvent: true });
        this.resetRightsSearchState();
        this.changeDetectorRef.markForCheck();
    }

    onRightsSearchEnter(event: Event): void {
        if (!this.currentUserrole) {
            return;
        }

        const term = this.getRightsSearchTerm();
        if (!term || !this.rightsSearchCycle.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const total = this.getRightsSearchTotalMatches();
        if (total <= 1) {
            this.rightsSearchCycleIndex = 0;
        } else {
            this.rightsSearchCycleIndex = (this.rightsSearchCycleIndex + 1) % total;
        }

        this.changeDetectorRef.markForCheck();
        this.scrollToSearchMatch(this.rightsSearchCycleIndex);
    }

    trackMenuByName(_index: number, menu: string): string {
        return menu;
    }

    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent): void {
        if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'f') {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.focusPageSearch();
    }

    focusPageSearch(): void {
        const input = this.currentUserrole ? this.rightsSearchInput : this.userroleInput;
        const nativeInput = input?.nativeElement;
        if (!nativeInput) {
            return;
        }
        nativeInput.focus();
        nativeInput.select();
    }

    getRightsSearchTerm(): string {
        return this.normalizeSearchText(this.rightsSearchCtrl.value);
    }

    onRightsSearchInput(): void {
        this.applyRightsSearch();
    }

    get visibleMenus(): string[] {
        const term = this.getRightsSearchTerm();
        if (!term || !this.rightsSearchCycle.length) {
            return this.menus;
        }
        const current = this.getCurrentSearchMatch();
        return current ? [current.menu] : this.menus;
    }

    getCurrentSearchMatch(): UnifiedSearchMatch | undefined {
        return this.rightsSearchCycle[this.rightsSearchCycleIndex];
    }

    getRightsSearchStatusText(): string {
        const total = this.getRightsSearchTotalMatches();
        if (!this.getRightsSearchTerm() || !this.rightsSearchCycle.length || total === 0) {
            return '';
        }

        const position = this.rightsSearchCycleIndex + 1;
        const match = this.getCurrentSearchMatch();
        if (!match) {
            return '';
        }

        if (match.level === 'menu') {
            if (total === 1) {
                return `Tab: ${match.menu}`;
            }
            return `${total} Treffer – ${position}/${total}: Tab ${match.menu}`;
        }

        if (match.level === 'group') {
            if (total === 1) {
                return `Block: ${match.group} (${match.menu})`;
            }
            return `${total} Treffer – ${position}/${total}: Block ${match.group} (${match.menu})`;
        }

        if (total === 1) {
            return `1 Treffer: ${this.getRightActionLabel(match.right)} (${match.right.benutzerrecht_id})`;
        }
        return `${total} Treffer – ${position}/${total}: ${this.getRightActionLabel(match.right)} (${match.right.benutzerrecht_id})`;
    }

    isMenuExpanded(menu: string): boolean {
        if (this.getRightsSearchTerm() && this.rightsSearchCycle.length) {
            const current = this.getCurrentSearchMatch();
            return !!current && this.menusWithSearchMatches.has(menu) && current.menu === menu;
        }
        return this.userExpandedMenus.has(menu);
    }

    isMenuVisible(menu: string): boolean {
        return this.visibleMenus.includes(menu);
    }

    isGroupVisible(menu: string, group: string): boolean {
        const term = this.getRightsSearchTerm();
        if (!term || !this.rightsSearchCycle.length) {
            return true;
        }

        const current = this.getCurrentSearchMatch();
        if (!current || current.menu !== menu) {
            return false;
        }

        if (current.level === 'menu') {
            return true;
        }

        return current.group === group;
    }

    onMenuOpened(menu: string): void {
        if (!this.getRightsSearchTerm()) {
            this.userExpandedMenus.add(menu);
        }
    }

    onMenuClosed(menu: string): void {
        if (!this.getRightsSearchTerm()) {
            this.userExpandedMenus.delete(menu);
        }
    }

    isRightVisible(rightId: number): boolean {
        if (!this.getRightsSearchTerm() || !this.rightsSearchCycle.length) {
            return true;
        }

        const current = this.getCurrentSearchMatch();
        if (!current) {
            return true;
        }

        if (current.level !== 'right') {
            return true;
        }

        return this.sameRightId(current.right?.benutzerrecht_id, rightId);
    }

    isRightHighlighted(rightId: number): boolean {
        const current = this.getCurrentSearchMatch();
        return current?.level === 'right' && this.sameRightId(current.right?.benutzerrecht_id, rightId);
    }

    isRightActive(rightId: number): boolean {
        return this.isRightHighlighted(rightId) && this.rightsSearchCycle.length > 1;
    }

    isGroupHighlighted(menu: string, group: string): boolean {
        const current = this.getCurrentSearchMatch();
        return (
            current?.level === 'group' &&
            current.menu === menu &&
            current.group === group &&
            this.rightsSearchCycle.length > 1
        );
    }

    getRightActionLabel(ur: Userright): string {
        return this.parseBezeichnung(ur.bezeichnung).action || ur.bezeichnung || '';
    }

    getMenuElementId(menu: string): string {
        return `userrole-menu-${this.encodeElementId(menu)}`;
    }

    getGroupElementId(menu: string, group: string): string {
        return `userrole-group-${this.encodeElementId(menu)}-${this.encodeElementId(group)}`;
    }

    private parseBezeichnung(bezeichnung: string): ParsedBezeichnung {
        const parts = String(bezeichnung ?? '').split(/\s+-\s+/);
        if (parts.length >= 3) {
            return { menu: parts[0], group: parts[1], action: parts[2] };
        }
        if (parts.length === 2) {
            return { menu: parts[0], group: parts[1], action: '' };
        }
        return { menu: parts[0] || '', group: '', action: '' };
    }

    private applyRightsSearch(): void {
        const term = this.getRightsSearchTerm();
        if (!term) {
            this.resetRightsSearchState();
            this.changeDetectorRef.markForCheck();
            return;
        }

        this.resolveRightsSearch(term);
        this.rightsSearchCycleIndex = 0;
        this.changeDetectorRef.markForCheck();

        if (this.rightsSearchCycle.length) {
            setTimeout(() => this.scrollToSearchMatch(0), 350);
            this.scheduleRightsSearchInputFocus(400);
        }
    }

    private resetRightsSearchState(): void {
        this.rightsSearchLevel = 'none';
        this.rightsSearchMatchedMenus = [];
        this.rightsSearchMatchedGroups = [];
        this.rightsSearchMatches = [];
        this.rightsSearchCycle = [];
        this.rightsSearchCycleIndex = 0;
        this.highlightedRightIds = new Set();
        this.menusWithSearchMatches = new Set();
        this.visibleGroupsByMenu = new Map();
    }

    private resolveRightsSearch(term: string): void {
        this.resetRightsSearchState();

        if (this.isNumericRightsSearchTerm(term)) {
            const right = this.userrights.find(entry => String(entry.benutzerrecht_id) === term);
            if (right) {
                const { menu, group } = this.parseBezeichnung(right.bezeichnung);
                this.rightsSearchMatches = [{ menu, group, right }];
                this.buildSearchCycle(
                    [],
                    [],
                    this.rightsSearchMatches,
                );
            }
            return;
        }

        const matchedMenus = this.menus.filter(menu => this.textIncludes(menu, term));
        const matchedMenuSet = new Set(matchedMenus);

        const matchedGroups: GroupSearchMatch[] = [];
        for (const menu of this.menus) {
            for (const group of this.groups[menu] ?? []) {
                if (group && this.textIncludes(group, term)) {
                    matchedGroups.push({ menu, group });
                }
            }
        }

        const matchedRights: RightsSearchMatch[] = [];
        for (const right of this.userrights) {
            if (this.rightMatchesSearchTerm(right, term, matchedMenuSet)) {
                const parsed = this.parseBezeichnung(right.bezeichnung);
                matchedRights.push({ menu: parsed.menu, group: parsed.group, right });
            }
        }

        this.buildSearchCycle(matchedMenus, matchedGroups, matchedRights);
    }

    private rightMatchesSearchTerm(
        right: Userright,
        term: string,
        matchedMenuNames: Set<string>,
    ): boolean {
        const parsed = this.parseBezeichnung(right.bezeichnung);
        const actionLabel = this.getRightActionLabel(right);

        if (!parsed.group && !parsed.action && matchedMenuNames.has(parsed.menu)) {
            return false;
        }

        if (parsed.action && this.textIncludes(parsed.action, term)) {
            return true;
        }

        return !!(actionLabel && this.textIncludes(actionLabel, term));
    }

    private buildSearchCycle(
        matchedMenus: string[],
        matchedGroups: GroupSearchMatch[],
        matchedRights: RightsSearchMatch[],
    ): void {
        this.rightsSearchMatchedMenus = matchedMenus;
        this.rightsSearchMatchedGroups = matchedGroups;
        this.rightsSearchMatches = matchedRights;

        const cycle: UnifiedSearchMatch[] = [
            ...matchedMenus.map(menu => ({ level: 'menu' as const, menu })),
            ...matchedGroups.map(entry => ({
                level: 'group' as const,
                menu: entry.menu,
                group: entry.group,
            })),
            ...matchedRights.map(entry => ({
                level: 'right' as const,
                menu: entry.menu,
                group: entry.group,
                right: entry.right,
            })),
        ];

        this.rightsSearchCycle = cycle;
        this.rightsSearchLevel = cycle.length ? cycle[0].level : 'none';

        this.menusWithSearchMatches = new Set([
            ...matchedMenus,
            ...matchedGroups.map(entry => entry.menu),
            ...matchedRights.map(entry => entry.menu),
        ]);

        this.highlightedRightIds = new Set(
            matchedRights.map(match => this.toRightId(match.right.benutzerrecht_id)),
        );

        this.visibleGroupsByMenu = new Map();
        for (const entry of matchedGroups) {
            if (!this.visibleGroupsByMenu.has(entry.menu)) {
                this.visibleGroupsByMenu.set(entry.menu, new Set());
            }
            this.visibleGroupsByMenu.get(entry.menu)?.add(entry.group);
        }
        for (const match of matchedRights) {
            if (!this.visibleGroupsByMenu.has(match.menu)) {
                this.visibleGroupsByMenu.set(match.menu, new Set());
            }
            this.visibleGroupsByMenu.get(match.menu)?.add(match.group);
        }
    }

    getRightsSearchTotalMatches(): number {
        return this.rightsSearchCycle.length;
    }

    private scrollToSearchMatch(index: number, retry = 0): void {
        this.changeDetectorRef.markForCheck();

        const scroll = () => {
            const match = this.rightsSearchCycle[index];
            if (!match) {
                return false;
            }

            if (match.level === 'menu') {
                document.getElementById(this.getMenuElementId(match.menu))?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                return true;
            }

            if (match.level === 'group') {
                document.getElementById(this.getMenuElementId(match.menu))?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                const groupElement = document.getElementById(this.getGroupElementId(match.menu, match.group ?? ''));
                groupElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                return !!groupElement;
            }

            document.getElementById(this.getMenuElementId(match.menu))?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            const container = document.getElementById(this.getRightElementId(match.right.benutzerrecht_id));
            if (!container) {
                return false;
            }
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
        };

        setTimeout(() => {
            const scrolled = scroll();
            if (!scrolled && retry < 4) {
                this.scrollToSearchMatch(index, retry + 1);
                return;
            }
            this.scheduleRightsSearchInputFocus();
        }, 300 + retry * 150);
    }

    private scheduleRightsSearchInputFocus(delay = 0): void {
        setTimeout(() => this.keepRightsSearchInputFocused(), delay);
    }

    private keepRightsSearchInputFocused(): void {
        const nativeInput = this.rightsSearchInput?.nativeElement;
        if (!nativeInput || !this.getRightsSearchTerm()) {
            return;
        }
        nativeInput.focus({ preventScroll: true });
    }

    private getRightElementId(rightId: number | string): string {
        return `userrole-right-${this.toRightId(rightId)}`;
    }

    private toRightId(rightId: unknown): number {
        return Number(rightId);
    }

    private sameRightId(left: unknown, right: unknown): boolean {
        return this.toRightId(left) === this.toRightId(right);
    }

    private encodeElementId(value: string): string {
        return encodeURIComponent(value);
    }

    private isNumericRightsSearchTerm(term: string): boolean {
        return /^\d+$/.test(term);
    }

    private normalizeSearchText(value: unknown): string {
        return String(value ?? '')
            .normalize('NFKC')
            .toLocaleLowerCase('de')
            .trim();
    }

    private textIncludes(haystack: string, normalizedTerm: string): boolean {
        return this.normalizeSearchText(haystack).includes(normalizedTerm);
    }
}
